// The funicular RIDES of this world (lifts.json `lines`, the WORLD stage's
// solved funicular runs): counterbalanced car pairs driven by the server's
// lift.s array, and platform-carry for players standing in a car. The static
// track is the bake's: the ballast ribbon + rails (ways2.js), the deck and the
// bore (structures.bin, structs2.js), the carve (spans).
import * as THREE from 'three';
import { t } from './i18n.js';
import './i18n/player.js';
import { funicularLines, samplePath } from '../../game/lifts.js';
import { buildFunicularCar } from './funicularcar.js';
import { fetchJsonOnce } from './fetchonce.js';
import { runSteps } from './decode.js';

export class Funicular {
  constructor(scene, baseUrl) {
    this.scene = scene;
    this.lines = [];        // { path, cars:[g,g], s, targetS, prev:[v3,v3] }
    this.ready = false;     // main.js builds station zones once ALL rides load
    this._baseUrl = baseUrl;
    this._loadP = null;     // start(): the load, once
  }

  // main.js starts the fetch in its load phase 1 (nothing is fetched by the
  // constructor, so the object can exist — and be wired — from boot on)
  start() {
    if (!this._loadP) this._loadP = this._load(this._baseUrl);
    return this._loadP;
  }

  async _load(baseUrl) {
    try {
      // lifts.json + livery.json: one fetch each, shared with the Aerialway
      const lifts = await fetchJsonOnce(`${baseUrl}/lifts.json`);
      if (!lifts) { this.ready = true; if (this.onReady) await this.onReady([]); return; }
      // per-line livery (bake livery.json, AI-profiled from reference photos):
      // the two St. Moritz funiculars are DIFFERENT railways with different
      // cars — without this they both wore the Corviglia blue/yellow.
      this.livery = await fetchJsonOnce(`${baseUrl}/livery.json`).catch(() => null);
      await runSteps(this._linesSteps(lifts), 'funicular.build');
      // onReady lets the app hook in once the lines exist (station zones).
      // ready flips FIRST: the hook checks every ride's flag to build zones
      // exactly once after the last loader finishes.
      this.ready = true;
      if (this.onReady) await this.onReady(this.lines.map((l) => l.path));
    } catch { this.ready = true; if (this.onReady) this.onReady([]); }
  }

  // the lines in STEPS (R8d6, decode.js runSteps: the cars were one task
  // with the aerial resolve after the Beam): a step per line — its two cars
  // (a car's canvas label + boxes, a few ms) and the push. A whole line per
  // step: a pushed line is posed by the next update(), so no car is ever
  // drawn unposed at the origin. The same code in the same order as the
  // one-shot loop (gate check-lifts.mjs `steps`).
  *_linesSteps(lifts) {
    // how many lines this world has is DATA, not a constant
    const cap = lifts.max_lines > 0 ? lifts.max_lines : Infinity;
    for (const path of funicularLines(lifts).slice(0, cap)) {
      this.lines.push({
        path,
        cars: [this._buildCar(path.name), this._buildCar(path.name)],
        s: 0, targetS: 0, prev: [null, null],
        // walkable interior (riders roam inside — stationzones clamps to
        // this box) and the ride-sign mount: on the END wall below the
        // windscreen (glass starts at 1.35), the side walls are glass
        innerHx: 1.0, innerHz: 2.4,
        signMount: { y: 1.05, z: 2.6, s: 0.9 },
      });
      yield;
    }
  }

  _buildCar(name) {
    // the car itself (livery, glass, doors): funicularcar.js, shared with the
    // dashboard's ride preview
    const prof = (this.livery && this.livery.lines && this.livery.lines[name]) || null;
    const g = buildFunicularCar(prof);
    const label = makeLabel(name);
    label.position.y = 3.3;
    g.add(label);
    this.scene.add(g);
    return g;
  }

  setServerS(sArr) {
    const arr = Array.isArray(sArr) ? sArr : [sArr];
    for (let i = 0; i < this.lines.length && i < arr.length; i++) {
      this.lines[i].targetS = arr[i];
    }
  }

  // nearest car distance (for boarding hints); riding flag from last update
  nearest(playerPos) {
    let best = Infinity;
    for (const line of this.lines) {
      for (const car of line.cars) {
        best = Math.min(best, car.position.distanceTo(playerPos));
      }
    }
    return best;
  }

  // returns world-space delta to apply to a rider at playerPos, or null
  update(dt, playerPos) {
    let carry = null;
    for (const line of this.lines) {
      line.s += (line.targetS - line.s) * Math.min(1, dt * 6);
      for (let i = 0; i < 2; i++) {
        const s = i === 0 ? line.s : 1 - line.s;
        const { pos, tangent } = samplePath(line.path, s, _samp);   // read here only
        const car = line.cars[i];
        _v.set(pos[0], pos[1] + 0.3, pos[2]);
        const prev = line.prev[i];
        car.position.copy(_v);
        _look.set(pos[0] + tangent[0], pos[1] + 0.3 + tangent[1], pos[2] + tangent[2]);
        car.lookAt(_look);
        if (prev) {
          _local.copy(playerPos).sub(_v);
          _localQ.copy(car.quaternion).invert();
          _local.applyQuaternion(_localQ);
          if (Math.abs(_local.x) < 1.5 && Math.abs(_local.z) < 3.0
            && _local.y > -1.5 && _local.y < 2.6) {
            // floorY: riders stand ON THE CAR FLOOR (origin +0.24), not on the
            // terrain below — standing on terrain decoupled the rider whenever
            // the ground dipped >1.5 m under the moving car
            carry = { x: _v.x - prev.x, y: _v.y - prev.y, z: _v.z - prev.z,
              floorY: _v.y + 0.24 };
          }
        }
        line.prev[i] = (prev || new THREE.Vector3()).copy(_v);
      }
    }
    return carry;
  }
}

export function makeLabel(text) {
  const cv = document.createElement('canvas');
  cv.width = 512; cv.height = 96;
  const g = cv.getContext('2d');
  g.fillStyle = 'rgba(20,22,28,0.72)';
  g.fillRect(0, 0, 512, 96);
  g.fillStyle = '#f3e9d2';
  g.font = 'bold 44px system-ui, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText((text || t('player.station.funicular')).slice(0, 26), 256, 48);
  const tex = new THREE.CanvasTexture(cv);
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: tex, transparent: true, depthTest: true,
  }));
  sprite.scale.set(4.2, 0.8, 1);
  return sprite;
}

const _v = new THREE.Vector3();
const _samp = { pos: [0, 0, 0], tangent: [0, 0, 0] };
const _look = new THREE.Vector3();
const _local = new THREE.Vector3();
const _localQ = new THREE.Quaternion();
